import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import TradingViewWidget, { TVHeatmap, TVMarketOverview, TVEconomicCalendar } from '@/components/app/TradingView';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { Lock } from '@phosphor-icons/react';

export function News() {
  const [news, setNews] = useState([]);
  const [cat, setCat] = useState('ALL');
  useEffect(() => { (async () => {
    const q = cat === 'ALL' ? '' : `?category=${cat}`;
    const { data } = await api.get('/news' + q); setNews(data);
  })(); }, [cat]);
  return (
    <div className="space-y-6" data-testid="news-page">
      <div><div className="data-label text-saffron">Market News</div><h1 className="font-heading text-3xl font-bold mt-1">News Feed</h1></div>
      <div className="flex gap-2 flex-wrap">
        {['ALL','FOREX','CRYPTO','GOLD','INDICES','ECONOMIC'].map(c => (
          <button key={c} onClick={() => setCat(c)} className={`px-4 py-2 rounded-md text-sm ${cat === c ? 'bg-saffron text-white' : 'glass hover:bg-white/10'}`}>{c}</button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {news.map(n => (
          <div key={n.id} className="glass p-6 rounded-lg neon-border">
            <div className="flex items-center justify-between"><div className="data-label text-saffron">{n.category}</div><div className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleDateString()}</div></div>
            <h3 className="font-heading font-bold text-lg mt-2">{n.title}</h3>
            <p className="text-sm text-muted-foreground mt-2">{n.summary}</p>
            <p className="text-sm mt-3">{n.content}</p>
            <div className="text-xs text-muted-foreground mt-3">— {n.source}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Analysis() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get('/analysis'); setItems(data); })(); }, []);
  return (
    <div className="space-y-6" data-testid="analysis-page">
      <div><div className="data-label text-saffron">Market Analysis</div><h1 className="font-heading text-3xl font-bold mt-1">Deep Dives</h1></div>
      {items.length === 0 && <div className="glass p-8 text-center text-muted-foreground rounded-lg">No analysis published yet.</div>}
      <div className="grid gap-4">
        {items.map(a => (
          <div key={a.id} className="glass p-6 rounded-lg neon-border">
            <div className="flex items-center gap-3 mb-2"><div className="data-label text-saffron">{a.category}</div><span className="font-mono text-xs">{a.asset}</span></div>
            <h3 className="font-heading font-bold text-xl">{a.title}</h3>
            {a.locked ? (
              <div className="mt-4 p-6 bg-surface-base rounded-md text-center"><Lock size={24} className="text-saffron mx-auto mb-2" weight="duotone" /><div className="text-sm text-muted-foreground">Upgrade to Premium to read</div></div>
            ) : (
              <>
                <p className="text-sm mt-3 whitespace-pre-wrap">{a.content}</p>
                {a.tradingview_link && <a href={a.tradingview_link} target="_blank" rel="noreferrer" className="text-saffron text-sm hover:underline mt-3 inline-block">View chart on TradingView →</a>}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Charts() {
  const [symbol, setSymbol] = useState('BINANCE:BTCUSDT');
  const symbols = [
    { s: 'BINANCE:BTCUSDT', l: 'BTC/USDT' }, { s: 'BINANCE:ETHUSDT', l: 'ETH/USDT' },
    { s: 'FX:EURUSD', l: 'EUR/USD' }, { s: 'FX:GBPUSD', l: 'GBP/USD' },
    { s: 'OANDA:XAUUSD', l: 'Gold' }, { s: 'FOREXCOM:SPXUSD', l: 'S&P 500' }, { s: 'NASDAQ:TSLA', l: 'Tesla' },
  ];
  return (
    <div className="space-y-6" data-testid="charts-page">
      <div><div className="data-label text-saffron">Live Charts</div><h1 className="font-heading text-3xl font-bold mt-1">TradingView Terminal</h1></div>
      <div className="flex gap-2 flex-wrap">
        {symbols.map(sy => (
          <button key={sy.s} onClick={() => setSymbol(sy.s)} className={`px-4 py-2 rounded-md text-sm font-mono ${symbol === sy.s ? 'bg-saffron text-white' : 'glass hover:bg-white/10'}`}>{sy.l}</button>
        ))}
      </div>
      <Tabs defaultValue="chart">
        <TabsList className="glass"><TabsTrigger value="chart">Chart</TabsTrigger><TabsTrigger value="overview">Overview</TabsTrigger><TabsTrigger value="heatmap">Heatmap</TabsTrigger><TabsTrigger value="calendar">Calendar</TabsTrigger></TabsList>
        <TabsContent value="chart" className="mt-4"><TradingViewWidget symbol={symbol} height={600} /></TabsContent>
        <TabsContent value="overview" className="mt-4"><TVMarketOverview /></TabsContent>
        <TabsContent value="heatmap" className="mt-4"><TVHeatmap /></TabsContent>
        <TabsContent value="calendar" className="mt-4"><TVEconomicCalendar /></TabsContent>
      </Tabs>
    </div>
  );
}

export function Referral() {
  const [data, setData] = useState({ code: '', stats: {} });
  useEffect(() => { (async () => { const r = await api.get('/referral/stats'); setData(r.data); })(); }, []);
  const link = `${window.location.origin}/signup?ref=${data.code}`;
  return (
    <div className="space-y-6 max-w-2xl" data-testid="referral-page">
      <div><div className="data-label text-saffron">Referral Program</div><h1 className="font-heading text-3xl font-bold mt-1">Invite & Earn</h1></div>
      <div className="glass p-6 rounded-lg neon-border">
        <div className="data-label">Your unique link</div>
        <div className="mt-2 flex gap-2">
          <input readOnly value={link} className="flex-1 bg-surface-base border border-white/10 rounded px-3 py-2 font-mono text-sm" data-testid="referral-link" />
          <button onClick={() => { navigator.clipboard.writeText(link); }} className="px-4 py-2 bg-saffron hover:bg-saffron-hover rounded-md text-white text-sm font-semibold">Copy</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['clicks','registrations','verified','rewards'].map(k => (
          <div key={k} className="glass p-4 rounded-lg"><div className="data-label capitalize">{k === 'rewards' ? 'Rewards ($)' : k}</div><div className="stat-num text-3xl mt-2 text-saffron">{data.stats?.[k] ?? 0}</div></div>
        ))}
      </div>
    </div>
  );
}
